import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Volume2, Play, Pause, X, FileText, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { ttsUrl } from "../lib/voice";
import { useI18n } from "../lib/i18n";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

export const ReadAloud = () => {
  const { t, lang } = useI18n();
  const screen = useLocation().pathname.replace(/^\//, "") || "dashboard";
  const audioRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [url, setUrl] = useState("");
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [err, setErr] = useState("");
  const [showTx, setShowTx] = useState(false);

  useEffect(() => {
    audioRef.current?.pause();
    setOpen(false); setSummary(""); setUrl(""); setPlaying(false);
  }, [screen]);

  useEffect(() => {
    const a = audioRef.current;
    if (!url || !a) return;
    a.src = url;
    a.playbackRate = rate;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = rate; }, [rate]);

  const run = async () => {
    audioRef.current?.pause();
    setOpen(true); setLoading(true); setErr(""); setSummary(""); setUrl(""); setPlaying(false);
    try {
      const { data } = await api.post("/summary", { screen, language: lang });
      setSummary(data.summary);
      try { setUrl(await ttsUrl(data.summary, lang)); } catch (_) { setErr(t("ttsFailed")); setShowTx(true); }
    } catch (e) { setErr(errMsg(e)); } finally { setLoading(false); }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a || !url) return;
    if (a.paused) a.play().then(() => setPlaying(true)).catch(() => {});
    else { a.pause(); setPlaying(false); }
  };

  return (
    <>
      <Button data-testid="read-aloud-speaker-control" variant="outline" size="icon" onClick={run}
        aria-label={t("readAloud")} title={t("readAloud")} className="rounded-xl border-slate-200">
        <Volume2 className="h-5 w-5" />
      </Button>
      <audio ref={audioRef} onEnded={() => setPlaying(false)} playsInline className="hidden" />
      {open && (
        <div data-testid="read-aloud-panel" className="fixed bottom-4 end-4 z-50 w-[min(420px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur-md">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Volume2 className="h-4 w-4 text-sky-600" /> {t("summaryTitle")}
            </div>
            <button data-testid="read-aloud-close" onClick={() => { audioRef.current?.pause(); setOpen(false); }} aria-label="close" className="rounded-lg p-1 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>
          {loading ? (
            <div data-testid="read-aloud-loading" className="flex items-center gap-2 py-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />{t("generating")}</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button data-testid="read-aloud-play-pause" size="sm" onClick={toggle} disabled={!url} className="rounded-xl bg-slate-900 hover:bg-slate-800">
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  <span className="ms-1">{playing ? t("pause") : t("play")}</span>
                </Button>
                <div className="flex items-center gap-1" role="group" aria-label={t("speed")}>
                  {SPEEDS.map((s) => (
                    <button key={s} data-testid={`read-aloud-speed-${s}`} onClick={() => setRate(s)}
                      className={`rounded-lg px-2 py-1 text-xs font-semibold transition-colors ${rate === s ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                      {s}x
                    </button>
                  ))}
                </div>
                <button data-testid="read-aloud-transcript-toggle" onClick={() => setShowTx((v) => !v)} className="ms-auto inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline">
                  <FileText className="h-3.5 w-3.5" />{t("transcript")}
                </button>
              </div>
              {err && <p data-testid="read-aloud-error" className="mt-3 text-xs text-rose-700">{err}</p>}
              {showTx && summary && (
                <p data-testid="read-aloud-transcript" className="mt-3 max-h-48 overflow-y-auto rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">{summary}</p>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};
